import React, { useCallback, useEffect, useState, forwardRef } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Icon, color } from "@wf/components";
import MainContainer from "../MainContainer";
import UserBar from "../UserBar";
import LanguageBar from "../LanguageBar";
import styles from "./SideBar.module.scss";

interface MenuItem {
  id: string;
  title: string;
  menu?: MenuItem[];
  isFirstItem?: boolean;
  isLastLevel?: boolean;
  url?: string;
  target?: string;
  isInternalLink?: boolean;
}

interface SideBarProps {
  menu: MenuItem[];
  isOpened: boolean;
  handleClose: () => void;
  mainLogo: string;
  children?: React.ReactNode;
  handleLinkClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, url: string, target?: string, isTradeLink?: boolean) => void;
}

// Define the SideBar component with TypeScript
const SideBar: React.FC<SideBarProps> = forwardRef(({ menu, isOpened, handleClose, handleLinkClick, mainLogo, children }, ref) => {
  const [navigationList, setNavigationList] = useState<MenuItem[]>([]);
  const [nextNavigationList, setNextNavigationList] = useState([]);

  const { t } = useTranslation("translation");
  const isMobile = useMediaQuery({ maxWidth: 767 });

  let userBar = null;
  let languageBar = null;

  React.Children.forEach(children, child => {
    // @ts-ignore
    if (child?.type === UserBar) {
      userBar = child;
    }
    // @ts-ignore
    if (child?.type === LanguageBar) {
      languageBar = child;
    }
  });

  const handleItemClick = useCallback(
      //   @ts-ignore
    (e, item, itemList) => {
      if (item.menu) {
        if (item.isFirstItem) {
          setNavigationList(item.isLastLevel ? nextNavigationList : menu);
        } else {
          setNextNavigationList(itemList);
          setNavigationList([
            {
              id: item.id,
              title: item.title,
              menu: item.menu,
              isFirstItem: true,
              isLastLevel: itemList[0].isFirstItem
            },
            ...item.menu
          ]);
        }
      } else {
        handleLinkClick(e, item.url, item.target, item.isInternalLink);
        handleClose();
      }
    },
    [handleLinkClick, handleClose, nextNavigationList, navigationList]
  );

  const handleCloseBtnClick = () => {
    handleClose();
    setNavigationList(menu);
  };

  useEffect(() => {
    setNavigationList(menu);
  }, [isOpened, menu]);

  return (
    //   @ts-ignore
    <div className={cn(styles.sideBar, { [styles.opened]: isOpened })} ref={ref}>
      {isMobile && (
        <div className={styles.topBar}>
          <MainContainer className={styles.topBarContainer}>{userBar}</MainContainer>
        </div>
      )}
      <div className={styles.header}>
        <MainContainer className={styles.headerContainer}>
          {!isMobile && <span className={styles.sidebarTitle}>{t("Menu")}</span>}
          {isMobile && <img className={styles.logo} src={mainLogo} alt="" />}
          <div className={styles.headerActions}>
            {isMobile && languageBar}
            <Icon
                //   @ts-ignore
              name="ic_close"
              onClick={handleCloseBtnClick}
              className={styles.closeBtn}
              size={22}
                //   @ts-ignore
              color={color("typography", "light")}
            />
          </div>
        </MainContainer>
      </div>
      <div className={styles.navigationContainer}>
        {navigationList.map(item => (
          <div
            key={item.id + item.title}
            className={cn(styles.navItem, { [styles.backItem]: item?.isFirstItem })}
            role="button"
            tabIndex={0}
            onClick={e => handleItemClick(e, item, navigationList)}
          >
            <MainContainer className={styles.itemContainer}>
              {item?.isFirstItem && (
                  //   @ts-ignore
                <Icon name="ic_arrow_left" className={styles.arrow} color={color("typography", "light")} />
              )}
              <span
                className={cn({
                  [styles.nestedItemTitle]:
                    !item?.isFirstItem && !item?.isLastLevel && menu.length !== navigationList.length
                })}
              >
                {item.title}
              </span>
              {!!item.menu && !item?.isFirstItem && (
                //   @ts-ignore
                <Icon name="ic_arrow_right" className={styles.arrow} color={color("typography", "light")} />
              )}
            </MainContainer>
          </div>
        ))}
      </div>
    </div>
  );
});

export default SideBar;
